import { aawzDate } from '@utils/dates/aawz-date'
import { taskStatusId } from '@modules/tarefas/utils/optimisticStatusMove'
import {
  FINAL_STATUS_CATEGORIES,
  LEGACY_STATUS_TO_CATEGORY,
} from '@modules/tarefas/constants/Text'

/**
 * Defensive parser for task date strings emitted by the backend.
 *
 * The backend can emit dates in multiple shapes (ISO 8601, raw `datetime`,
 * DD/MM/YYYY HH:mm, etc.). We try each known format explicitly first, then
 * fall back to the default `aawzDate(value)` constructor which handles ISO
 * and timestamps natively. Returns the first valid `aawzDate` instance, or
 * `null` when nothing parses.
 *
 * @param {string|Date|number} value
 * @returns {object|null}
 */
// Counts the number of numeric placeholders (YYYY, MM, DD, HH, mm, ss, SS)
// declared in a format mask. Used to gate `aawzDate(value, format)` calls so
// we only attempt parsing with formats whose placeholder count matches the
// input's numeric-run count. This avoids the silent JS Date fallback inside
// parseDate.js (which would happily interpret "04/05/2026" as April 5 via
// the US locale string parser when the chosen format mismatches).
function countFormatTokens(format) {
  const match = format.match(/(YYYY|MM|DD|HH|mm|ss|SS)/g)
  return match ? match.length : 0
}

function countNumericRuns(value) {
  const match = value.match(/\d+/g)
  return match ? match.length : 0
}

export function parseTaskDate(value) {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'string') {
    // Format insertion order: longer/timed formats first, so the
    // token-count gate below picks the most-specific matching format
    // (e.g., "04/05/2026 10:00" matches HH:mm before falling through to
    // the date-only DD/MM/YYYY format).
    const formats = [
      'DD/MM/YYYY HH:mm:ss',
      'DD/MM/YYYY HH:mm',
      'YYYY-MM-DD HH:mm:ss',
      'DD/MM/YYYY',
      'YYYY-MM-DD',
    ]
    const runs = countNumericRuns(value)
    for (const fmt of formats) {
      // Only try a format whose token count matches the input's numeric-run
      // count. Without this gate, parseDate.js silently falls back to
      // `new Date(value)` (US locale) and returns a valid-but-wrong date.
      if (countFormatTokens(fmt) !== runs) continue
      const parsed = aawzDate(value, fmt)
      if (parsed && parsed.isValid()) return parsed
    }
  }
  const fallback = aawzDate(value)
  return fallback && fallback.isValid() ? fallback : null
}

/**
 * Resolves a task's status category from a `statusId -> statusObject` map
 * (the owning folder's statuses slice). Falls back to the transitional
 * `task.status` literal mapping when the id is unresolved. Returns `null`
 * when nothing resolves.
 *
 * @param {object} task
 * @param {object} [statusMap] - `{ [statusId]: { category, ... } }`
 * @returns {string|null}
 */
export function resolveTaskStatus(task, statusMap) {
  if (!task) return null
  const sid = taskStatusId(task)
  if (sid != null && statusMap && statusMap[sid]) {
    return statusMap[sid].category || null
  }
  if (task.status != null) return LEGACY_STATUS_TO_CATEGORY[task.status] || null
  return null
}

// Pure date predicate: due date strictly before today (start-of-day). Used by
// both overdue helpers. Defensive against malformed/empty dates → false.
function _isPastDue(task) {
  const due = task.dueDate == null ? task.due_date : task.dueDate
  const parsed = parseTaskDate(due)
  if (!parsed) return false
  return parsed.startOf('day').valueOf() < aawzDate().startOf('day').valueOf()
}

/**
 * Category-aware overdue check (customizar-status-tarefas). A task is overdue
 * when its due date is strictly in the past AND its status category is NOT
 * `done`/`closed`. The category is resolved against the folder statuses map
 * (or the transitional `task.status` literal). Same-day boundary → NOT
 * overdue. Replaces the hardcoded `status==='done'||'closed'` check (AC-10).
 *
 * @param {object} task
 * @param {object} [statusMap] - `{ [statusId]: { category, ... } }`
 * @returns {boolean}
 */
export function isOverdueWithStatuses(task, statusMap) {
  if (!task) return false
  const category = resolveTaskStatus(task, statusMap)
  if (category && FINAL_STATUS_CATEGORIES[category]) return false
  return _isPastDue(task)
}

/**
 * Backwards-compatible overdue check used where the folder statuses map is
 * not available (e.g. a single task row). Resolves the category from the
 * transitional `task.status` literal only. Prefer `isOverdueWithStatuses`
 * when the folder statuses slice is on hand.
 *
 * @param {object} task
 * @returns {boolean}
 */
export function isOverdue(task) {
  if (!task) return false
  return isOverdueWithStatuses(task, null)
}

/**
 * Returns `true` when the task is a parent task (no parent association).
 * Defensive for both backend casings (`parentId` and `parent_id`).
 *
 * @param {object} task
 * @returns {boolean}
 */
export function isParentTask(task) {
  if (!task) return false
  return task.parentId == null && task.parent_id == null
}
